import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import CustomHeader from "../../../components/Header/CustomHeader";
import CustomTable from "../../../components/Table/CustomTable";
import { STRINGS } from "../strings";
import { PROMPT_HEADERS } from "../../../utils/constants";
import { styles } from "../styles";
import CustomStyledMenu from "../../../components/Menu/CustomStyledMenu";
import { Box } from "@mui/material";
import Button from "../../../components/Buttons/Button";
import SearchField from "../../../components/InputFields/SearchField";
import SkeletonLoader from "../../../components/Loaders/SkeletonLoader";
import { ICON_LABELS } from "../../../utils/constants/UI";
import CreateCommonFolder from "../../Marketing/components/common/CreateCommonFolder";
import {
  AI_EMAIL_MARKETING_CREATE_NEW_PROMPT_PATH,
  AI_EMAIL_MARKETING_PROMPT_GENERATE_PATH,
} from "../../../utils/constants/routes";
import PromptDeleteModal from "../components/PromptDeleteModal";
import { AI_MARKETING } from "../../../utils/constants/httpConstants";
import { getRequest, postRequest } from "../../../Apis/apiWrapper";
import { useSelector, useDispatch } from "react-redux";
import { paginationDataAction } from "../../../redux/actions/customTableActions";
import { getPromptListData } from "../../../redux/actions/promptActions";
import { EMITTER_ERROR } from "../../../utils/constants";
import { generateBreadCrumbDescription } from "../../../utils/helpers";
import EventEmitter from "../../../utils/emitter/EventEmitter";

const PromptListingForEM = () => {
  const classes = styles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();

  // State variables
  const [showItemDelete, setShowItemDelete] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [operationRecordPayload, setOperationRecordPayload] = useState(null);
  const [isCloning, setIsCloning] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [isMount, setIsMount] = useState(false);
  const [headerItems, setHeaderItems] = useState([
    {
      id: STRINGS.PROMPT_HEADER,
      label: STRINGS.PROMPT_LIST,
      path: AI_EMAIL_MARKETING_PROMPT_GENERATE_PATH,
    },
  ]);

  // Redux selectors
  const paginationSelector = useSelector((state) => state.getPaginationQuery);
  const getPromptListSelector = useSelector((state) => state.getPromptListData);

  // // 🔥 TEMPORARY DUMMY DATA (REMOVE WHEN BACKEND IS READY)
  // // When you revert : Change the getPromptListSelector from let to const.
  // getPromptListSelector = {
  //   loading: false,
  //   data: {
  //     total_records: 3,
  //     data: {
  //       records: [
  //         { _id: "1", name: "Welcome Email Prompt", type: "prompt" },
  //         { _id: "2", name: "Follow-up Email Prompt", type: "prompt" },
  //         { _id: "3", name: "Cold Outreach Prompt", type: "prompt" },
  //       ],
  //     },
  //   },
  // };
  // // 🔥 TEMPORARY DUMMY DATA END
  
  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  const handleShowItemDelete = (row) => {
    setOperationRecordPayload(row);
    setShowItemDelete(true);
  };

  const handleNavigateToCreatePrompt = () => {
    navigate(AI_EMAIL_MARKETING_CREATE_NEW_PROMPT_PATH);
  };

  const handleViewPrompt = (row) => {
    navigate(AI_EMAIL_MARKETING_CREATE_NEW_PROMPT_PATH, {
      state: { isViewing: true, promptId: row._id || row.id },
    });
  };

  // Clone prompt function
  const handleClonePrompt = async (row) => {
    try {
      setIsCloning(true);
      const promptId = row._id || row.id;
      const cloneApiUrl = `${AI_MARKETING.duplicatePrompt}${promptId}`;
      const response = await postRequest(navigate, cloneApiUrl, {});
      if (response?.status === 200) {
        // Refresh data after successful clone
        dispatch(getPromptListData(paginationSelector?.data?.query, navigate));
        console.log("Prompt cloned successfully");
      } else {
        console.error("Failed to clone prompt");
      }
    } catch (error) {
      console.error("Error cloning prompt:", error);
    } finally {
      setIsCloning(false);
    }
  };

  // Search handler - following ListsScreenForEM pattern
  const handleSearch = () => {
    try {
      dispatch(
        paginationDataAction({
          ...paginationSelector?.data,
          checkedRow: [], // Reset checked rows on search
          query: {
            ...paginationSelector?.data?.query,
            search: searchValue,
            page: 1, // Reset to first page on search
          },
        })
      );
    } catch (e) {
      EventEmitter.emit(EMITTER_ERROR, "Some Error Occurred.");
    }
  };

  // const handleSort = (sortId, direction) => {
  //   dispatch(
  //     paginationDataAction({
  //       ...paginationSelector?.data,
  //       checkedRow: [],
  //       query: {
  //         ...paginationSelector?.data?.query,
  //         sort: sortId,
  //         direction: direction,
  //         page: 1,
  //       },
  //     })
  //   )
  // }

  const handleShowItemEdit = (row) => {
    navigate(AI_EMAIL_MARKETING_CREATE_NEW_PROMPT_PATH, {
      state: { isEditing: true, promptId: row._id || row.id },
    });
  };

  // Load folder name when `id` changes
  useEffect(() => {
    const loadBreadcrumb = async () => {
      if (id) {
        try {
          const response = await getRequest(
            navigate,
            `${AI_MARKETING.getFolderDetail}?id=${id}`
          );
          const folderName = response?.data?.data?.name || "Untitled Folder";
          setHeaderItems((prev) => {
            const newItems = [...prev];
            if (newItems.length > 1) newItems.pop();
            newItems.push({ id, label: folderName });
            return newItems;
          });
        } catch (error) {
          console.error("Error fetching folder name:", error);
        }
      } else {
        setHeaderItems([
          {
            id: STRINGS.PROMPT_HEADER,
            label: STRINGS.PROMPT_LIST,
            path: AI_EMAIL_MARKETING_PROMPT_GENERATE_PATH,
          },
        ]);
      }
    };
    loadBreadcrumb();
  }, [id, navigate]);

  // Main data fetching useEffect - following ListsScreenForEM pattern
  useEffect(() => {
    if (getPromptListSelector?.loading) return;
    (async () => {
      try {
        if (id) {
          console.log("Fetching prompt list data with id:", id);
          await dispatch(
            getPromptListData(
              {
                ...paginationSelector?.data?.query,
                parent: id,
              },
              navigate
            )
          );
        } else {
          await dispatch(
            getPromptListData({ ...paginationSelector?.data?.query }, navigate)
          );
        }
      } catch (error) {
        console.error("Error in useEffect:", error);
      }
    })();
  }, [paginationSelector?.data?.query, id]);

  // Reset search when navigating
  useEffect(() => {
    setSearchValue("");
    setIsMount(false);
  }, [navigate]);

  // Debounced search effect - following ListsScreenForEM pattern
  useEffect(() => {
    if (isMount) {
      let getData;
      getData = setTimeout(() => {
        handleSearch();
      }, 300);
      return () => clearTimeout(getData);
    } else {
      setIsMount(true);
    }
  }, [searchValue]);

  // Table configuration
  const promptTableProps = {
    heads: PROMPT_HEADERS,
    body: getPromptListSelector?.data?.data?.records || [],
    actions: {
      marketing_list: {
        function: handleViewPrompt,
        loading: false,
      },
    },
    operations: [
      {
        id: STRINGS.CLONE,
        label: STRINGS.COMMON_CLONE || "Clone",
        iconLabel: ICON_LABELS.CONTENT_COPY,
        function: handleClonePrompt,
        loading: isCloning,
      },
      {
        id: STRINGS.EDIT,
        label: STRINGS.COMMON_EDIT,
        iconLabel: ICON_LABELS.EDIT,
        function: handleShowItemEdit,
      },
      {
        id: STRINGS.SMALL_DELETE,
        label: STRINGS.DELETE,
        iconLabel: ICON_LABELS.DELETE,
        variant: "error",
        function: handleShowItemDelete,
      },
    ],
    total_records: getPromptListSelector?.data?.total_records || 0,
    isLoading: getPromptListSelector?.loading,
    sortHandler: {},
  };

  return (
    <Box className={classes.tableHead}>
      <CustomHeader
        items={headerItems}
        description={
          getPromptListSelector?.loading
            ? STRINGS.LOADING
            : generateBreadCrumbDescription(getPromptListSelector?.data)
        }
      />
      <div className={classes.tableUi}>
        <Box className={classes.folderRightSide}>
          <Box className={classes.displayFlex}>
            <div className={classes.HeadSub}>
              <Box className={classes.GAP}>
                <SearchField
                  autoComplete="off"
                  variant={STRINGS.DEFAULT}
                  size={"sm36"}
                  LeftIcon={"search"}
                  placeholder={STRINGS.SEARCH_TEXT}
                  value={searchValue}
                  onChange={(e) => {
                    setSearchValue(e.target.value);
                  }}
                />
              </Box>
            </div>
            <div>
              <div className={classes.HeadSub}>
                <Button
                  LeftIcon={ICON_LABELS.CREATE_NEW_FOLDER}
                  label={`${STRINGS.NEW_FOLDER}`}
                  size={"sm36"}
                  variant={"ghost"}
                  onClick={handleOpenModal}
                />
                <Button
                  LeftIcon={ICON_LABELS.ADD}
                  label={`${STRINGS.NEW_PROMPT}`}
                  size={"sm36"}
                  variant={"primary"}
                  onClick={handleNavigateToCreatePrompt}
                />
              </div>
              <CustomStyledMenu
                marginTop={"10px"}
                width={"12.156vw"}
                isRightAlign={true}
                noMargin={true}
                arrow={true}
              >
                <div className={classes.filterered}>
                  {Array.from({ length: 5 }).map((_, index) => (
                    <div key={index} className={classes.skeletonPadding}>
                      <SkeletonLoader
                        type="rounded"
                        skeletonWidth={"100%"}
                        skeletonHeight={"30px"}
                        stackWidth={"100%"}
                      />
                    </div>
                  ))}
                </div>
              </CustomStyledMenu>
            </div>
          </Box>
        </Box>
        <div className={classes.innerTable}>
          <CustomTable tableData={promptTableProps} />
        </div>
      </div>

      {/* Modals */}
      <CreateCommonFolder
        open={isModalOpen}
        setOpen={handleCloseModal}
        type={STRINGS.TYPE_CAMP}
        recordId={"Test"}
        nameFold={"Work"}
      />
      {showItemDelete && (
        <PromptDeleteModal
          open={true}
          setOpen={setShowItemDelete}
          IDs={
            operationRecordPayload
              ? [operationRecordPayload.id || operationRecordPayload._id]
              : []
          }
          val={STRINGS.PROMPT_LIST}
          itemName={operationRecordPayload?.name}
          itemType={operationRecordPayload?.type}
        />
      )}
    </Box>
  );
};

export default PromptListingForEM;
